import { POST_TEXT_LIMIT } from '../../../packages/domain/src/23-post-continuations.mjs';
import { continuationPreview } from '../ui/16-post-continuations.mjs';
// Owns composer rendering and interactions.
// Dependencies are injected by app.mjs; feature modules never import one another.
/** Create transient publishing forms; the server validates and persists submitted drafts. */
export function createComposer(ctx) {
  function iconChoices(title, choices, initial) {
    const group = ctx.e('fieldset', 'icon-choice-group');
    group.append(ctx.e('legend', '', ctx.uiText(title)));
    const row = ctx.e('div', 'icon-choice-grid');
    const name = 'choice-' + crypto.randomUUID();
    for (const [value, label, symbol] of choices) {
      const input = ctx.field('input', {
        type: 'radio',
        name,
        value,
        checked: value === initial,
      });
      const tile = ctx.e('label', 'icon-choice');
      tile.append(input, ctx.icon(symbol), ctx.e('span', '', ctx.uiText(label)));
      row.append(tile);
    }
    group.append(row);
    return {
      node: group,
      get value() {
        return row.querySelector('input:checked').value;
      },
    };
  }

  async function composer(kind = 'post') {
    const openingActor = ctx.state?.me.id,
      openingRoute = ctx.route().join('/');
    try {
      if (ctx.ensureFullState && !(await ctx.ensureFullState())) return;
      if (ctx.state?.me.id !== openingActor || ctx.route().join('/') !== openingRoute) return;
    } catch (error) {
      ctx.toast(error.message);
      return;
    }
    const feature = kind === 'story' ? 'stories' : kind === 'reel' ? 'reels' : 'publishing';
    if (!ctx.featureEnabled(feature)) {
      ctx.toast('Publishing this format is unavailable on this server.');
      return;
    }
    const form = ctx.e('form', 'form-stack');
    let processing = false;
    const editor = new ctx.MediaEditor({
      reel: kind === 'reel',
      onBusy: (value) => {
        processing = value;
        for (const control of form.querySelectorAll('button,input,textarea'))
          control.disabled = value;
      },
    });
    ctx.modal.addEventListener('close', () => editor.destroy(), { once: true });
    const text = ctx.field('textarea', {
      placeholder:
        kind === 'story'
          ? 'A little story behind this photo…'
          : `Share a moment, ${ctx.state.me.name.split(' ')[0]}…`,
      required: true,
      maxLength: POST_TEXT_LIMIT,
      ariaLabel: 'Post text',
    });
    const audience = iconChoices(
      'Who can see it?',
      [
        ['friends', 'Friends', 'users'],
        ['friends-of-friends', ctx.translate('social.audience.friends-of-friends'), 'share-2'],
        ['family', ctx.translate('social.familyAudience'), 'heart'],
        ['close-friends', 'Close circle', 'star'],
        ['public', 'Public', 'globe'],
        ['only-me', 'Only me', 'lock'],
      ],
      'friends',
    );
    const tone = iconChoices(
      'The feeling',
      [
        ['calm', 'Calm', 'leaf'],
        ['connected', 'Connected', 'heart'],
        ['inspired', 'Inspired', 'sparkles'],
        ['energized', 'Energized', 'zap'],
      ],
      'connected',
    );
    const lifetime = iconChoices(
      'Keep it or let it disappear',
      kind === 'story'
        ? [['24h', '24 hours', 'timer']]
        : [
            ['permanent', 'Keep it', 'infinity'],
            ['1h', '1 hour', 'clock'],
            ['24h', '24 hours', 'timer'],
            ['7d', '7 days', 'calendar-days'],
          ],
      kind === 'story' ? '24h' : 'permanent',
    );
    const tagged = ctx.e('div', 'tag-choices');
    const tagSearch = ctx.field('input', {
      type: 'search',
      placeholder: 'Find a friend to tag',
      ariaLabel: 'Find friends to tag',
    });
    const tagGrid = ctx.e('div', 'tag-grid');
    ctx.state.friends
      .filter((id) => !ctx.state.settings.blocked.includes(id))
      .forEach((id) => {
        const friend = ctx.who(id);
        const check = ctx.field('input', { type: 'checkbox', value: id });
        const tile = ctx.append(
          ctx.e('label', 'tag-choice'),
          check,
          ctx.avatar(friend),
          ctx.e('span', '', friend.name),
        );
        tile.dataset.search = friend.name.toLowerCase();
        check.addEventListener('change', () => {
          if (tagGrid.querySelectorAll('input:checked').length > 10) {
            check.checked = false;
            ctx.toast('Tag up to ten friends.');
          }
        });
        tagGrid.append(tile);
      });
    tagSearch.addEventListener('input', () => {
      for (const tile of tagGrid.children)
        tile.hidden =
          !tile.dataset.search.includes(tagSearch.value.trim().toLowerCase()) &&
          !tile.querySelector('input').checked;
    });
    tagged.append(tagSearch, tagGrid);
    const tagDetails = ctx.append(
      ctx.e('details', 'composer-extra'),
      ctx.append(
        ctx.e('summary'),
        ctx.icon('user-plus'),
        document.createTextNode(ctx.translate('post.tags')),
      ),
      ctx.e('p', 'subtle', 'Select up to ten connected friends.'),
      tagged,
    );
    const fields = ctx.append(ctx.e('div', 'composer-selections'), audience.node, tone.node);
    const photos = ctx.e('div', 'photo-choices');
    for (const [name, label] of Object.entries(ctx.imageNames)) {
      const b = ctx.button(
        '',
        async () => {
          try {
            await editor.addDemo(name);
          } catch (error) {
            ctx.toast(error.message);
          }
        },
        'photo-choice',
      );
      b.setAttribute('aria-label', `Add generated ${label} photo`);
      b.append(
        ctx.field('img', {
          src: `/assets/${name}-160.webp`,
          alt: '',
          loading: 'lazy',
        }),
        ctx.e('span', '', label),
      );
      photos.append(b);
    }
    const error = ctx.e('p', 'form-error');
    error.setAttribute('role', 'alert');
    const locationInput = ctx.field('input', {
      placeholder: 'A place or city (optional)',
      maxLength: 100,
      ariaLabel: 'Place or city',
    });

    const pollEnabled = ctx.field('input', {
      type: 'checkbox',
      ariaLabel: 'Add a poll',
    });
    pollEnabled.disabled = !ctx.featureEnabled('polls');
    const pollFields = ctx.e('div', 'form-stack');
    pollFields.hidden = true;
    const pollQuestion = ctx.field('input', {
      maxLength: 200,
      placeholder: 'What would you like to ask?',
      ariaLabel: 'Poll question',
    });
    const pollOptions = [1, 2, 3, 4].map((i) =>
      ctx.field('input', {
        maxLength: 100,
        placeholder: `Choice ${i}${i > 2 ? ' (optional)' : ''}`,
        ariaLabel: `Poll choice ${i}`,
      }),
    );
    const pollDuration = iconChoices(
      'Voting closes after',
      [
        [1, '1 hour', 'clock'],
        [24, '24 hours', 'timer'],
        [168, '7 days', 'calendar-days'],
      ],
      24,
    );
    pollFields.append(pollQuestion, ...pollOptions, pollDuration.node);
    pollEnabled.addEventListener('change', () => {
      pollFields.hidden = !pollEnabled.checked;
      pollQuestion.required = pollEnabled.checked;
      pollOptions.slice(0, 2).forEach((n) => (n.required = pollEnabled.checked));
    });
    const submit = ctx.e(
      'button',
      'primary-button',
      ctx.uiText(
        kind === 'story' ? 'Share story →' : kind === 'reel' ? 'Share reel →' : 'Share moment →',
      ),
    );
    submit.type = 'submit';
    form.append(
      text,
      continuationPreview(ctx, text, { story: kind === 'story' }),
      fields,
      ctx.e(
        'p',
        'subtle',
        'Close circle shares only with friends you have added to your circle. Following does not grant private access.',
      ),
    );
    if (kind !== 'story')
      form.append(
        ctx.append(
          ctx.e('label', 'poll-toggle'),
          ctx.icon('circle-check'),
          document.createTextNode(ctx.translate('post.poll')),
          pollEnabled,
          ctx.e('span', 'switch-track'),
        ),
        pollFields,
      );
    form.append(
      lifetime.node,
      ctx.e(
        'p',
        'subtle',
        'Expiring moments leave feeds when their time is up. People can still take screenshots.',
      ),
      tagDetails,
      editor.node,
      ctx.append(
        ctx.e('details', 'composer-extra'),
        ctx.append(
          ctx.e('summary'),
          ctx.icon('image'),
          document.createTextNode(ctx.translate('post.demoPhotos')),
        ),
        photos,
      ),
      locationInput,
      error,
      submit,
    );
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (processing) return;
      error.textContent = '';
      submit.disabled = true;
      try {
        const media = await editor.export();
        submit.disabled = true;
        if (kind === 'reel' && !media.uploads.length)
          throw new Error('Add photos or a video to make your reel.');
        if (
          kind === 'story' &&
          (media.uploads.length !== 1 || media.uploads[0].data.startsWith('data:video/'))
        )
          throw new Error(
            'Use one photo for a story. Share multi-photo moments and videos as posts or reels.',
          );
        await ctx.command(
          {
            type: 'publish',
            kind,
            poll: pollEnabled.checked
              ? {
                  question: pollQuestion.value,
                  options: pollOptions.map((n) => n.value.trim()).filter(Boolean),
                  hours: Number(pollDuration.value),
                }
              : undefined,
            lifetime: lifetime.value,
            taggedUserIds: [...tagGrid.querySelectorAll('input:checked')].map((o) => o.value),
            text: text.value,
            audience: audience.value,
            tone: tone.value,
            mediaUploads: media.uploads.length ? media.uploads : undefined,
            title: media.title,
            layout: media.layout,
            leadMediaIndex: media.leadMediaIndex,
            music: media.music,
            musicMixed: media.musicMixed,
            location: locationInput.value,
          },
          { paint: false },
        );
        ctx.feed.reset(ctx.mode);
        ctx.feedScroll = 0;
        ctx.modal.close();
        ctx.go(kind === 'reel' ? 'reels' : 'feed');
        ctx.render();
        ctx.toast(
          kind === 'story'
            ? 'Your story is shared for 24 hours.'
            : kind === 'reel'
              ? 'Your reel is shared.'
              : 'Your moment is shared.',
        );
      } catch (err) {
        error.textContent = err.message;
      } finally {
        submit.disabled = false;
      }
    });

    ctx.dialog(
      kind === 'story'
        ? 'Share a little story'
        : kind === 'reel'
          ? 'Make a reel'
          : 'Share a moment',
      form,
    );
  }
  return { iconChoices, composer };
}
